import React, { useState, useEffect, useCallback } from 'react';
import { getBlogPosts, deleteBlogPost, updateBlogPost, BlogPost } from '../services/blog';
import AnimatedSection from './AnimatedSection';
import BlogUpload from './BlogUpload';

const BlogManagement: React.FC = () => {
  const [blogPosts, setBlogPosts] = useState<BlogPost[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [editingPost, setEditingPost] = useState<BlogPost | null>(null);
  const [showUpload, setShowUpload] = useState(false);
  const [filters, setFilters] = useState({
    category: '',
  });
  const [pagination, setPagination] = useState({ total: 0, page: 1, limit: 20, pages: 1 });

  const loadBlogPosts = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await getBlogPosts({
        category: filters.category || undefined,
        limit: pagination.limit,
        page: pagination.page,
      });
      setBlogPosts(response.data);
      setPagination(prev => ({ ...prev, ...response.pagination }));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load blog posts');
    } finally {
      setLoading(false);
    }
  }, [filters.category, pagination.limit, pagination.page]);

  useEffect(() => {
    loadBlogPosts();
  }, [loadBlogPosts]);

  const handleDelete = async (id: string) => {
    if (!window.confirm('Are you sure you want to delete this blog post?')) {
      return;
    }

    try {
      await deleteBlogPost(id);
      setBlogPosts(posts => posts.filter(post => post._id !== id));
      loadBlogPosts();
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Failed to delete blog post');
    }
  };

  const handleEdit = (post: BlogPost) => {
    setEditingPost(post);
    setShowUpload(false);
  };

  const handleUpdate = async (formData: FormData | Partial<BlogPost>) => {
    if (!editingPost) return;

    try {
      await updateBlogPost(editingPost._id, formData);
      await loadBlogPosts();
      setEditingPost(null);
      alert('Blog post updated successfully!');
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Failed to update blog post');
    }
  };

  const handleCancelEdit = () => {
    setEditingPost(null);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  const truncateContent = (content: string, maxLength: number = 150) => {
    if (content.length <= maxLength) return content;
    return content.substring(0, maxLength) + '...';
  };

  return (
    <section className="py-20 brand-bg min-h-screen">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <AnimatedSection animation="slide-up">
          <div className="mb-8">
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-4">
              <div>
                <h1 className="text-4xl md:text-5xl font-bold text-dark-900 dark:text-neutral-50 mb-2">
                  Blog <span className="text-primary-500">Management</span>
                </h1>
                <p className="text-xl text-dark-600 dark:text-neutral-300">
                  Manage your blog posts
                </p>
              </div>
              <button
                onClick={() => {
                  setShowUpload(!showUpload);
                  setEditingPost(null);
                }}
                className="px-6 py-3 bg-primary-500 hover:bg-primary-600 text-white font-medium rounded-lg transition-colors"
              >
                {showUpload ? 'Hide Upload' : '+ Create New Post'}
              </button>
            </div>
          </div>
        </AnimatedSection>

        {/* Upload Section */}
        {showUpload && !editingPost && (
          <div className="mb-8">
            <BlogUpload onUploadSuccess={loadBlogPosts} />
          </div>
        )}

        {/* Edit Form */}
        {editingPost && (
          <AnimatedSection animation="fade-in">
            <div className="bg-white/80 dark:bg-dark-800/80 backdrop-blur-sm rounded-xl p-6 border border-primary-500/20 mb-8">
              <div className="flex justify-between items-center mb-6">
                <h3 className="text-2xl font-bold text-dark-900 dark:text-neutral-50">
                  Edit Blog Post
                </h3>
                <button
                  onClick={handleCancelEdit}
                  className="text-dark-600 dark:text-neutral-400 hover:text-dark-900 dark:hover:text-neutral-50"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
              <EditBlogForm post={editingPost} onUpdate={handleUpdate} onCancel={handleCancelEdit} />
            </div>
          </AnimatedSection>
        )}

        {/* Filters */}
        <AnimatedSection animation="fade-in" delay={100}>
          <div className="bg-white/80 dark:bg-dark-800/80 backdrop-blur-sm rounded-xl p-6 border border-primary-500/20 mb-8">
            <h3 className="text-lg font-semibold text-dark-900 dark:text-neutral-50 mb-4">Filters</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-dark-600 dark:text-neutral-300 mb-2">
                  Category
                </label>
                <input
                  type="text"
                  value={filters.category}
                  onChange={(e) => {
                    setFilters({ ...filters, category: e.target.value });
                    setPagination(prev => ({ ...prev, page: 1 }));
                  }}
                  placeholder="Filter by category"
                  className="w-full px-4 py-2 rounded-lg border border-primary-500/20 bg-white dark:bg-dark-900 text-dark-900 dark:text-neutral-50"
                />
              </div>
              <div className="flex items-end">
                <button
                  onClick={() => {
                    setFilters({ category: '' });
                    setPagination(prev => ({ ...prev, page: 1 }));
                  }}
                  className="w-full px-4 py-2 bg-primary-500 hover:bg-primary-600 text-white rounded-lg transition-colors"
                >
                  Clear Filters
                </button>
              </div>
            </div>
          </div>
        </AnimatedSection>

        {/* Blog Posts List */}
        <AnimatedSection animation="fade-in" delay={200}>
          {loading ? (
            <div className="text-center py-20">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-500 mx-auto mb-4"></div>
              <p className="text-dark-600 dark:text-neutral-300">Loading blog posts...</p>
            </div>
          ) : error ? (
            <div className="bg-red-500/10 border border-red-500/50 text-red-600 dark:text-red-400 rounded-lg p-4 text-center">
              {error}
            </div>
          ) : blogPosts.length === 0 ? (
            <div className="bg-white/80 dark:bg-dark-800/80 backdrop-blur-sm rounded-xl p-12 border border-primary-500/20 text-center">
              <p className="text-dark-600 dark:text-neutral-300 text-lg">No blog posts found.</p>
            </div>
          ) : (
            <>
              <div className="bg-white/80 dark:bg-dark-800/80 backdrop-blur-sm rounded-xl p-6 border border-primary-500/20 mb-4">
                <h3 className="text-xl font-bold text-dark-900 dark:text-neutral-50 mb-4">
                  Blog Posts ({pagination.total})
                </h3>
                <div className="space-y-4">
                  {blogPosts.map((post) => (
                    <div
                      key={post._id}
                      className="border border-primary-500/10 rounded-lg p-4 hover:bg-primary-500/5 transition-colors"
                    >
                      <div className="flex flex-col md:flex-row gap-4">
                        {post.imageUrl && (
                          <div className="w-full md:w-32 h-32 rounded-lg overflow-hidden flex-shrink-0">
                            <img
                              src={post.imageUrl}
                              alt={post.title}
                              className="w-full h-full object-cover"
                              onError={(e) => {
                                const target = e.target as HTMLImageElement;
                                target.src = 'https://via.placeholder.com/128';
                              }}
                            />
                          </div>
                        )}
                        <div className="flex-1">
                          <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-2 mb-2">
                            <div>
                              <h4 className="text-lg font-bold text-dark-900 dark:text-neutral-50">
                                {post.title}
                              </h4>
                              {post.category && (
                                <span className="inline-block mt-1 px-2 py-1 bg-primary-500/20 text-primary-600 dark:text-primary-400 rounded text-xs font-medium">
                                  {post.category}
                                </span>
                              )}
                            </div>
                            <div className="text-sm text-dark-500 dark:text-neutral-400">
                              {formatDate(post.publishedAt)}
                            </div>
                          </div>
                          <p className="text-sm text-dark-600 dark:text-neutral-300 mb-3 line-clamp-2">
                            {truncateContent(post.content)}
                          </p>
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-4 text-sm text-dark-500 dark:text-neutral-400">
                              <span className="flex items-center gap-1">
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                                </svg>
                                {post.views} views
                              </span>
                            </div>
                            <div className="flex gap-2">
                              <button
                                onClick={() => handleEdit(post)}
                                className="p-2 text-primary-500 hover:bg-primary-500/10 rounded-lg transition-colors"
                                title="Edit"
                              >
                                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                                </svg>
                              </button>
                              <button
                                onClick={() => handleDelete(post._id)}
                                className="p-2 text-red-500 hover:bg-red-500/10 rounded-lg transition-colors"
                                title="Delete"
                              >
                                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                </svg>
                              </button>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Pagination */}
              {pagination.pages > 1 && (
                <div className="flex items-center justify-center gap-4 mt-6">
                  <button
                    onClick={() => setPagination(prev => ({ ...prev, page: Math.max(1, prev.page - 1) }))}
                    disabled={pagination.page === 1}
                    className="px-4 py-2 bg-primary-500 hover:bg-primary-600 disabled:opacity-50 disabled:cursor-not-allowed text-white rounded-lg transition-colors"
                  >
                    Previous
                  </button>
                  <span className="text-sm text-dark-600 dark:text-neutral-400">
                    Page {pagination.page} of {pagination.pages}
                  </span>
                  <button
                    onClick={() => setPagination(prev => ({ ...prev, page: Math.min(pagination.pages, prev.page + 1) }))}
                    disabled={pagination.page === pagination.pages}
                    className="px-4 py-2 bg-primary-500 hover:bg-primary-600 disabled:opacity-50 disabled:cursor-not-allowed text-white rounded-lg transition-colors"
                  >
                    Next
                  </button>
                </div>
              )}
            </>
          )}
        </AnimatedSection>
      </div>
    </section>
  );
};

// Edit Form Component
interface EditBlogFormProps {
  post: BlogPost;
  onUpdate: (data: FormData | Partial<BlogPost>) => void;
  onCancel: () => void;
}

const EditBlogForm: React.FC<EditBlogFormProps> = ({ post, onUpdate, onCancel }) => {
  const [formData, setFormData] = useState({
    title: post.title,
    content: post.content,
    category: post.category || '',
    publishedAt: post.publishedAt ? new Date(post.publishedAt).toISOString().split('T')[0] : '',
  });
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [loading, setLoading] = useState(false);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    try {
      if (imageFile) {
        // If new image file, use FormData
        const formDataToSend = new FormData();
        formDataToSend.append('image', imageFile);
        formDataToSend.append('title', formData.title);
        formDataToSend.append('content', formData.content);
        formDataToSend.append('category', formData.category);
        if (formData.publishedAt) {
          formDataToSend.append('publishedAt', new Date(formData.publishedAt).toISOString());
        }
        onUpdate(formDataToSend);
      } else {
        // Otherwise, use JSON
        const updateData: Partial<BlogPost> = {
          title: formData.title,
          content: formData.content,
          category: formData.category,
        };
        if (formData.publishedAt) {
          updateData.publishedAt = new Date(formData.publishedAt).toISOString();
        }
        onUpdate(updateData);
      }
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Failed to update blog post');
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div>
        <label className="block text-sm font-medium text-dark-900 dark:text-neutral-50 mb-2">
          Featured Image - Leave empty to keep current
        </label>
        <input
          type="file"
          accept="image/*"
          onChange={(e) => setImageFile(e.target.files?.[0] || null)}
          className="w-full px-4 py-2 border border-primary-500/20 rounded-lg bg-white dark:bg-dark-900 text-dark-900 dark:text-neutral-50"
        />
        {post.imageUrl && (
          <p className="text-xs text-dark-500 dark:text-neutral-400 mt-1">
            Current: <a href={post.imageUrl} target="_blank" rel="noopener noreferrer" className="text-primary-500 hover:underline">{post.imageUrl.substring(0, 50)}...</a>
          </p>
        )}
      </div>

      <div>
        <label className="block text-sm font-medium text-dark-900 dark:text-neutral-50 mb-2">
          Title *
        </label>
        <input
          type="text"
          name="title"
          value={formData.title}
          onChange={handleInputChange}
          required
          className="w-full px-4 py-2 border border-primary-500/20 rounded-lg bg-white dark:bg-dark-900 text-dark-900 dark:text-neutral-50"
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-dark-900 dark:text-neutral-50 mb-2">
          Content *
        </label>
        <textarea
          name="content"
          value={formData.content}
          onChange={handleInputChange}
          required
          rows={10}
          className="w-full px-4 py-2 border border-primary-500/20 rounded-lg bg-white dark:bg-dark-900 text-dark-900 dark:text-neutral-50"
        />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-dark-900 dark:text-neutral-50 mb-2">
            Category
          </label>
          <input
            type="text"
            name="category"
            value={formData.category}
            onChange={handleInputChange}
            className="w-full px-4 py-2 border border-primary-500/20 rounded-lg bg-white dark:bg-dark-900 text-dark-900 dark:text-neutral-50"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-dark-900 dark:text-neutral-50 mb-2">
            Published Date
          </label>
          <input
            type="date"
            name="publishedAt"
            value={formData.publishedAt}
            onChange={handleInputChange}
            className="w-full px-4 py-2 border border-primary-500/20 rounded-lg bg-white dark:bg-dark-900 text-dark-900 dark:text-neutral-50"
          />
        </div>
      </div>

      <div className="flex gap-4">
        <button
          type="submit"
          disabled={loading}
          className="flex-1 py-3 px-6 bg-primary-500 hover:bg-primary-600 disabled:opacity-50 disabled:cursor-not-allowed text-white font-medium rounded-lg transition-colors"
        >
          {loading ? 'Updating...' : 'Update Blog Post'}
        </button>
        <button
          type="button"
          onClick={onCancel}
          className="px-6 py-3 bg-dark-600 hover:bg-dark-700 text-white font-medium rounded-lg transition-colors"
        >
          Cancel
        </button>
      </div>
    </form>
  );
};

export default BlogManagement;

